import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import ReactQuill from "react-quill";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import { AddTemplateFormProps, EditTemplateFormProps, SelectedTemplateProps } from "../../models/TemplateMessage";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";

export const SelectedTemplate = ({
  language,
  selectedTemplate,
  deleteTemplate,
  selectForEditing,
}: SelectedTemplateProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    deleteTemplate(selectedTemplate.id);
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex">
          <h3 className="mb-3 me-4">{selectedTemplate.name}</h3>
          <div className="xs-regular smoke-gray-font">
            {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.TEMPLATE_INFORMATION.CREATED_ON").replace(
              "{{DATE}}",
              formatDateHumanly(language, selectedTemplate.dt_created)
            )}
          </div>
          <div className="xs-regular smoke-gray-font mx-3"> - </div>
          <div className="xs-regular smoke-gray-font me-1">
            {selectedTemplate.dt_last_updated === null ? (
              <span>
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.TEMPLATE_INFORMATION.CREATED_BY").replace(
                  "{{USER}}",
                  selectedTemplate.author.full_name
                )}
              </span>
            ) : (
              <span>
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.TEMPLATE_INFORMATION.UPDATED_BY_USER_ON")
                  .replace("{{USER}}", selectedTemplate.author.full_name)
                  .replace("{{DATE}}", formatDateHumanly(language, selectedTemplate.dt_last_updated))}
              </span>
            )}
          </div>
        </div>
        <DstButton
          btnClass="text-decoration-none"
          value={translate(language, "GLOBAL.EDIT")}
          btnImageBefore={<span className="dst-icon-edit-05 pe-2 align-text-bottom text-decoration-none" />}
          variant="link"
          color="var(--color-blue)"
          clickFunction={() => selectForEditing(selectedTemplate)}
        />
      </div>

      <div
        className="gray-font s-regular text-pre-wrap"
        id="message-content"
        dangerouslySetInnerHTML={{ __html: selectedTemplate.content }}
      />
      <DstButton
        btnClass="text-decoration-none mt-3 px-0"
        value={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.DELETE")}
        btnImageAfter={<span className="dst-icon-trash-03 ps-2 align-text-bottom text-decoration-none" />}
        variant="link"
        color="var(--color-red)"
        clickFunction={() => setShowDeleteModal(true)}
      />
      <DstConfirm
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.POPUP_DELETION.CONFIRM_DELETE_TITLE")}
        messageConfirm={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.POPUP_DELETION.CONFIRM_DELETE_TEXT")}
        confirmTextButton={translate(language, "GLOBAL.DELETE")}
      />
    </div>
  );
};

export const AddTemplateForm = ({
  language,
  templateName,
  templateContent,
  poles,
  programs,
  selectedPole,
  selectedProgram,
  setSelectedPole,
  setSelectedProgram,
  setSelectedLanguage,
  selectedLanguage,
  setTemplateName,
  setTemplateContent,
  handleAddTemplateSubmit,
  handleCancel,
}: AddTemplateFormProps) => {
  return (
    <div className="add-template-form">
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.TITLE")}
            </Form.Label>
            <Form.Select
              onChange={(e) => {
                setSelectedLanguage(e.target.value === "null" ? null : e.target.value);
              }}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              <option value="en-US">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.ENGLISH")}
              </option>
              <option value="fr-FR">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.FRENCH")}
              </option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_POLE")}
            </Form.Label>
            <Form.Select
              onChange={(e) => setSelectedPole(e.target.value === "null" ? null : +e.target.value)}
              value={selectedPole || undefined}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              {poles.map((pole) => (
                <option key={pole.id} value={pole.id}>
                  {pole.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_PROGRAM")}
            </Form.Label>
            <Form.Select
              onChange={(e) => setSelectedProgram(e.target.value === "null" ? null : +e.target.value)}
              value={selectedProgram || undefined}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <input
        type="text"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
        placeholder={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.PLACEHOLDER_NAME")}
        className="form-control my-2"
      />
      <ReactQuill value={templateContent} onChange={setTemplateContent} />
      <div className="mt-2 d-flex justify-content-between">
        <DstButton
          btnClass="text-decoration-none px-0"
          value={translate(language, "GLOBAL.CANCEL")}
          variant="link"
          color="var(--color-black)"
          clickFunction={handleCancel}
        />
        <DstButton
          value={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.CONFIRM_CREATE_TEMPLATE")}
          clickFunction={handleAddTemplateSubmit}
        />
      </div>
    </div>
  );
};

export const EditTemplateForm = ({
  selectedTemplate,
  editingTemplateContent,
  editingTemplateName,
  language,
  poles,
  programs,
  selectedPole,
  selectedProgram,
  setSelectedPole,
  setSelectedProgram,
  setSelectedLanguage,
  setEditingTemplateContent,
  setEditingTemplateName,
  submitEdit,
  handleCancel,
  showForm,
}: EditTemplateFormProps) => {
  useEffect(() => {
    if ((selectedTemplate && selectedPole < 0) || selectedProgram < 0) {
      const selectedPoleTemp = selectedTemplate ? poles.find((pole) => pole.id === selectedTemplate.pole) : null;
      const selectedProgramTemp = selectedTemplate
        ? programs.find((program) => program.id === selectedTemplate.program)
        : null;

      if (selectedPoleTemp) {
        setSelectedPole(selectedPoleTemp.id);
      }

      if (selectedProgramTemp) {
        setSelectedProgram(selectedProgramTemp.id);
      }
    }
  }, [selectedTemplate, setSelectedPole, setSelectedProgram, selectedPole, selectedProgram, poles, programs]);

  return showForm ? (
    <div className="add-template-form">
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.TITLE")}
            </Form.Label>
            <Form.Select
              onChange={(e) => {
                setSelectedLanguage(e.target.value === "null" ? null : e.target.value);
              }}
              defaultValue={selectedTemplate?.language || "null"}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              <option value="en-US">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.ENGLISH")}
              </option>
              <option value="fr-FR">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_LANGUAGE.FRENCH")}
              </option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_POLE")}
            </Form.Label>
            <Form.Select
              onChange={(e) => setSelectedPole(e.target.value === "null" ? null : +e.target.value)}
              defaultValue={selectedTemplate?.pole || undefined}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              {poles.map((pole) => (
                <option key={pole.id} value={pole.id}>
                  {pole.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="m-regular">
              {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_PROGRAM")}
            </Form.Label>
            <Form.Select
              onChange={(e) => setSelectedProgram(e.target.value === "null" ? null : +e.target.value)}
              defaultValue={selectedTemplate?.program || undefined}
              className="s-regular"
            >
              <option value="null">
                {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SELECT_NONE")}
              </option>
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <input
        type="text"
        value={editingTemplateName}
        onChange={(e) => setEditingTemplateName(e.target.value)}
        placeholder={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.PLACEHOLDER_NAME")}
        className="form-control my-2"
      />
      <ReactQuill value={editingTemplateContent} onChange={setEditingTemplateContent} />
      <div className="mt-2 d-flex justify-content-between">
        <DstButton
          btnClass="text-decoration-none px-0"
          value={translate(language, "GLOBAL.CANCEL")}
          variant="link"
          color="var(--color-black)"
          clickFunction={handleCancel}
        />
        <DstButton
          value={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.SAVE_CHANGES")}
          clickFunction={submitEdit}
        />
      </div>
    </div>
  ) : null;
};
